import { useState, useEffect } from "react";
import { useUser } from "../../context/UserContext";
import { UXDataTable } from "../../components/UXDataTable";
import Sample from "../../api/LIMS/Sample";
import { applyFiltersToArray } from "../../global";
import { ColumnConfig, CsvExportPayload, Filter, FilterOption, MenuItem } from "./types";
import { getSampleCopyRouteState } from "./sampleCopy";

export const mySamplesFilterOptions: FilterOption[] = [
  { name: "sampleName", displayName: "MIDAS #", type: "midasNumber", enumValues: [] },
  { name: "description", displayName: "Description", type: "string", enumValues: [] },
  { name: "chemID", displayName: "Chem ID", type: "string", enumValues: [] },
  { name: "createdDate", displayName: "Date Created", type: "date", enumValues: [] },
];

const MySamplesCols: ColumnConfig[] = [
  { field: "sampleName", headerName: "MIDAS #", type: "button-midasnumber", path: "/newSample" },
  { field: "description", headerName: "Description", type: "label" },
  { field: "chemID", headerName: "Chem ID", type: "label" },
  { field: "createdDate", headerName: "Date Created", type: "datetime" },
];

export interface MySamplesTableProps {
  filters: Filter[];
  onExportPayloadChange: (csvPayLoad: CsvExportPayload) => void;
}

export const MySamplesTable = ({ filters, onExportPayloadChange }: MySamplesTableProps) => {
  const [samplesIsLoading, setSamplesIsLoading] = useState<boolean>(true);
  const [mySamples, setMySamples] = useState<Sample[]>([]);
  const [filteredSamples, setFilteredSamples] = useState<Sample[]>([]);
  const { username, isInternalUser } = useUser();
  const noDataMessage = `No samples found for owner ${username}`;

  const menuItemsSamples: MenuItem[] = [
    {
      menuType: "link-newSampleIncludingContainers",
      redirectPath: "/newSample",
      text: "Register Sample from Copy",
      onClickFunction: null,
      getRouteState: (sample) => getSampleCopyRouteState(sample, false),
    },
    {
      menuType: "link-newSampleIncludingContainers",
      redirectPath: "/newSample",
      text: "Register Sample from Copy Including Containers",
      onClickFunction: null,
      getRouteState: (sample) => getSampleCopyRouteState(sample, true),
    },
    ...(isInternalUser
      ? [
          {
            menuType: "link-requestTest",
            redirectPath: "/newTest",
            text: "Request a Test",
            onClickFunction: null,
          },
        ]
      : []),
  ];

  useEffect(() => {
    let cancelPromise = false;

    async function getData(perPage: number, page: number, allData: any[] = []): Promise<any[]> {
      if (cancelPromise) return allData;

      const response = await Sample.getMySamples(page, perPage);
      const data = response ? response.result : null;

      if (data !== null) {
        allData = [...allData, ...data];
      }

      if (data === null || data.length === 0) {
        setSamplesIsLoading(false);
        return allData;
      } else {
        page++;
        setMySamples(allData);
        setFilteredSamples(allData);
        return getData(perPage, page, allData);
      }
    }

    getData(200, 1);

    return () => {
      cancelPromise = true;
    };
  }, [username]);

  useEffect(() => {
    const newFilteredSamples = applyFiltersToArray(filters, mySamples);
    setFilteredSamples(newFilteredSamples);
    // const csvPayload = prepareExportPayload();
    // onExportPayloadChange(csvPayload);
  }, [mySamples, filters]);

  useEffect(() => {
    const csvPayload = prepareExportPayload();
    onExportPayloadChange(csvPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSamples, samplesIsLoading]);

  const prepareExportPayload = () => {
    const csvExport: CsvExportPayload = {
      fileName: "My Samples",
      objList: filteredSamples.map((sample) => {
        return {
          "MIDAS Number": sample.sampleName,
          Description: sample.description,
          "Chem ID": sample.chemID,
          "Date Created": sample.createdDate,
        };
      }),
      disabled: samplesIsLoading || filteredSamples.length === 0,
    };
    return csvExport;
  };

  return (
    <>
      <UXDataTable
        tableWidth="100%"
        cols={MySamplesCols}
        rows={filteredSamples === null ? [] : filteredSamples}
        moreOptionsCell={true}
        enablePaging={true}
        noDataMessage={noDataMessage}
        menuProps={menuItemsSamples}
        defaultRowsPerPage={10}
        isDataLoading={samplesIsLoading}
        tableName={"sampleInfo"}
        enableSorting={true}
        dataRefreshTrigger={undefined}
        setDataRefreshTrigger={undefined}
      />
    </>
  );
};
